import { Link, matchPath, useLocation } from "react-router";
import { my_routes } from "../../router/all_routes";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./dashboard.css";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import { usePermissions } from "../../../core/common/context/PermissionsUserContext";
import { useSelector } from "react-redux";

type DashboardButton = {
  type?: "button"; // bouton simple
  route: string;
  icon: string;
  label: string;
  resource: Resource;
  actions: Action[];
};

type DashboardDropdownItem = {
  route: string;
  icon: string;
  label: string;
  resource: Resource;
  actions: Action[];
};

type DashboardDropdown = {
  type: "dropdown"; // dropdown
  label: string;
  icon: string;
  items: DashboardDropdownItem[];
};

type DashboardItemType = DashboardButton | DashboardDropdown;

const DashboardMenu = () => {
  const routes = my_routes;
  const location = useLocation();

  // les permissions de l'utilisateur connecté
  const { permissions } = usePermissions();

  const isSuperAdmin = useSelector(
    (state: any) => state.auth.user.isSuperAdmin,
  );

  const isActive = (path: string) => {
    return matchPath({ path, end: true }, location.pathname);
  };

  const dashboardConfig: DashboardItemType[] = [
    {
      type: "button",
      route: routes.allStudent,
      icon: "ti ti-user-check",
      label: "Étudiants",
      resource: Resource.STUDENT,
      actions: [Action.CREATE, Action.READ, Action.UPDATE, Action.DELETE],
    },
    {
      type: "button",
      route: routes.allParent,
      icon: "ti ti-users",
      label: "Parents",
      resource: Resource.PARENT,
      actions: [Action.CREATE, Action.READ, Action.UPDATE, Action.DELETE],
    },
    {
      type: "button",
      route: routes.allTeacher,
      icon: "ti ti-users",
      label: "Enseignants",
      resource: Resource.TEACHER,
      actions: [Action.CREATE, Action.READ, Action.UPDATE, Action.DELETE],
    },
    {
      type: "button",
      route: routes.studentAbsence,
      icon: "ti ti-calendar-x",
      label: "Absences",
      resource: Resource.STUDENT_ABSENCE,
      actions: [Action.CREATE, Action.READ, Action.UPDATE, Action.DELETE],
    },
    {
      type: "button",
      route: routes.sanctions,
      icon: "ti ti-alert-triangle",
      label: "Sanctions",
      resource: Resource.STUDENT_SANCTION,
      actions: [Action.CREATE, Action.READ, Action.UPDATE, Action.DELETE],
    },
    {
      type: "button",
      route: routes.timeTableList,
      icon: "ti ti-calendar",
      label: "EDT",
      resource: Resource.EDT,
      actions: [Action.CREATE, Action.READ, Action.UPDATE, Action.DELETE],
    },
    {
      type: "dropdown",
      label: "Plus",
      icon: "ti ti-dots",
      items: [
        {
          route: routes.levelList,
          icon: "ti ti-user",
          label: "Niveaux",
          resource: Resource.LEVEL,
          actions: [Action.CREATE, Action.READ, Action.UPDATE, Action.DELETE],
        },
        {
          route: routes.classList,
          icon: "ti ti-users-group",
          label: "Classes",
          resource: Resource.CLASS,
          actions: [Action.CREATE, Action.READ, Action.UPDATE, Action.DELETE],
        },
        {
          route: routes.subjectList,
          icon: "ti ti-book",
          label: "Matières",
          resource: Resource.SUBJECT,
          actions: [Action.CREATE, Action.READ, Action.UPDATE, Action.DELETE],
        },
        {
          route: routes.classroomList,
          icon: "ti ti-building",
          label: "Salles",
          resource: Resource.CLASSROOM,
          actions: [Action.CREATE, Action.READ, Action.UPDATE, Action.DELETE],
        },
        {
          route: routes.trombinoscope,
          label: "Trombinoscope",
          icon: "fa fa-camera",
          resource: Resource.STUDENT,
          actions: [Action.READ, Action.UPDATE],
        },
      ],
    },
  ];

  const DashboardItem = ({ item }: { item: DashboardItemType }) => {
    // Dropdown ?
    if (item.type === "dropdown") {
      // Ne garder que les sous-éléments que l'utilisateur peut voir
      const visibleSubItems = item.items.filter(
        (sub) =>
          Object.values(Action).some((a) =>
            permissions.includes(buildPermission(sub.resource, a)),
          ) || isSuperAdmin,
      );

      // Si aucun sous-élément n'est visible, ne pas afficher le dropdown
      if (visibleSubItems.length === 0) return null;

      const isActiveDropdown = visibleSubItems.some((i) => isActive(i.route));

      return (
        <div className="dashboard-menu-item">
          <div className="dropdown h-100 dashboard-dropdown">
            <div
              className={`dashboard-nav-link dashboard-btn dropdown-toggle ${
                isActiveDropdown ? "active" : ""
              }`}
              data-bs-toggle="dropdown"
              style={{ cursor: "pointer" }}
            >
              <i className={item.icon} />
              <span>{item.label}</span>
            </div>
            <ul className="dropdown-menu dashboard-dropdown-menu">
              {visibleSubItems.map((sub) => (
                <Link
                  key={sub.route}
                  className={`dropdown-item d-flex align-items-center ${
                    isActive(sub.route) ? "active" : ""
                  }`}
                  to={sub.route}
                >
                  <i className={`${sub.icon} me-2`} />
                  <span>{sub.label}</span>
                </Link>
              ))}
            </ul>
          </div>
        </div>
      );
    }

    // Bouton simple
    const ProtectedButton = withPermission(
      () => (
        <div className="dashboard-menu-item">
          <Link
            to={item.route}
            className={`dashboard-nav-link dashboard-btn ${
              isActive(item.route) ? "active" : ""
            }`}
          >
            <i className={item.icon} />
            <span>{item.label}</span>
          </Link>
        </div>
      ),
      item.actions.map((a) => buildPermission(item.resource, a)),
      "masquer",
    );

    return <ProtectedButton />;
  };

  return (
    <>
      <div className="dashboard-module-menu">
        {dashboardConfig.map((item: DashboardItemType) => (
          <DashboardItem key={item.label} item={item} />
        ))}
      </div>
    </>
  );
};

export default DashboardMenu;
